/* MRPIECE — campaign landing page controller.
 *
 * Owns: quick add, its own cart drawer, offer progress, second-item
 * recommendations, the duo builder, chip filters, Load more, the sticky bar
 * and quick view.
 *
 * Money rule, and it is the important one: every currency figure the shopper
 * sees inside the cart drawer comes straight out of Shopify's /cart.js response
 * (original_total_price, total_discount, total_price). This file never computes
 * a discount. If the merchant edits the Shopify discount and forgets the
 * Customizer copy, the drawer still shows the truth and only the promo wording
 * goes stale — never the other way round.
 *
 * Meta pixel: deliberately no fbq() calls here. Shopify's own web pixel already
 * emits product_added_to_cart for every /cart/add.js, so firing AddToCart from
 * this file would double-count it. Funnel events go to dataLayer only.
 */
(function () {
  'use strict';

  /* Belt and braces. The script tag lives only in the Engine section so this
     file is parsed once, but a stray duplicate would otherwise attach a second
     delegated click listener and make every Add fire twice. */
  if (window.__mpCapsBooted) return;
  window.__mpCapsBooted = true;

  var BOOT = false;
  var CFG = {};
  var CATALOG = [];
  var CROSS = [];
  var cart = null;
  var lastAddedHandle = null;

  /* ------------------------------------------------------------------ utils */

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Shopify money_format renderer. Handles the placeholder set Shopify actually
  // ships; falls back to a plain grouped number if the format string is odd.
  function money(cents) {
    var fmt = CFG.moneyFormat || '{{amount}}';
    var v = Number(cents || 0) / 100;
    function group(n, dec, sep) {
      var f = n.toFixed(dec);
      var p = f.split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return p.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) {
      switch (k) {
        case 'amount': return group(v, 2, ',');
        case 'amount_no_decimals': return group(Math.round(v), 0, ',');
        case 'amount_with_comma_separator': return group(v, 2, '.').replace(/\.(\d{2})$/, ',$1');
        case 'amount_no_decimals_with_comma_separator': return group(Math.round(v), 0, '.');
        case 'amount_with_space_separator': return group(v, 2, ' ').replace(/\.(\d{2})$/, ',$1');
        case 'amount_no_decimals_with_space_separator': return group(Math.round(v), 0, ' ');
        case 'amount_with_apostrophe_separator': return group(v, 2, "'");
        default: return group(v, 2, ',');
      }
    });
  }

  function track(name, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name, mp_surface: 'caps_sale_lp' }, payload || {}));
    } catch (e) { /* analytics must never break the funnel */ }
  }

  /* Which cart lines count towards the offer.

     "*" means every line counts. That is not a shortcut — it is the only
     correct answer on a page whose offer is the store-wide "10% off any 2
     items", and on a clothing catalogue that spans T-Shirts, Polo Shirts,
     Shirts, Knitwear, Dresses and Sets. Counting one product type there would
     under-report what the shopper has already earned and print "add 2 more"
     over a cart Shopify has already discounted. */
  function isCap(line) {
    var want = String(CFG.capType || 'caps').toLowerCase();
    if (want === '*' || want === 'any') return true;
    return String(line.product_type || '').toLowerCase() === want;
  }

  function capUnits(c) {
    if (!c || !c.items) return 0;
    return c.items.reduce(function (n, l) { return n + (isCap(l) ? l.quantity : 0); }, 0);
  }

  // The offer tier the cart currently sits in, and the next one up.
  function tiers() { return (CFG.tiers || []).slice().sort(function (a, b) { return a.qty - b.qty; }); }

  function currentTier(units) {
    var hit = null;
    tiers().forEach(function (t) { if (units >= t.qty) hit = t; });
    return hit;
  }

  function nextTier(units) {
    var up = null;
    tiers().forEach(function (t) { if (!up && units < t.qty) up = t; });
    return up;
  }

  /* ------------------------------------------------------------------- cart */

  function getCart() {
    return fetch(CFG.routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { cart = c; syncSticky(); return c; });
  }

  function addToCart(variantId, qty) {
    return fetch(CFG.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty || 1 }] })
    }).then(function (r) {
      return r.json().then(function (b) {
        if (!r.ok) throw new Error(b && b.description ? b.description : 'add_failed');
        return b;
      });
    });
  }

  function changeLine(key, qty) {
    return fetch(CFG.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); }).then(function (c) { cart = c; syncSticky(); return c; });
  }

  /* --------------------------------------------------------- recommendations */

  function inCart(handle) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (l) { return l.handle === handle; });
  }

  function anchorCap() {
    if (lastAddedHandle) {
      var m = CATALOG.filter(function (p) { return p.handle === lastAddedHandle; })[0];
      if (m) return m;
    }
    if (!cart || !cart.items) return null;
    for (var i = cart.items.length - 1; i >= 0; i--) {
      if (isCap(cart.items[i])) {
        var h = cart.items[i].handle;
        var f = CATALOG.filter(function (p) { return p.handle === h; })[0];
        if (f) return f;
        return { handle: h, colors: [], brand: '', price: cart.items[i].price };
      }
    }
    return null;
  }

  /* Rank the best "second item" for the shopper.
     Deliberately NOT "four more black caps": a different colour scores highest,
     the same colour is penalised, and the final list is capped at one item per
     colour and two per brand so the row always looks like a real choice. */
  function recommendCaps(limit) {
    var anchor = anchorCap();
    var pool = CATALOG.filter(function (p) { return p.available && !inCart(p.handle); });
    if (!pool.length) return [];

    var scored = pool.map(function (p, idx) {
      var s = 0;
      if (anchor) {
        var ac = anchor.colors || [], pc = p.colors || [];
        var shares = pc.some(function (c) { return ac.indexOf(c) > -1; });
        if (ac.length && pc.length) { s += shares ? -3 : 4; }
        if (anchor.brand && p.brand) { s += anchor.brand === p.brand ? 2 : 0.5; }
        var ap = anchor.price || 0;
        if (ap) {
          var d = Math.abs(p.price - ap) / ap;
          if (d <= 0.25) s += 3; else if (d <= 0.5) s += 1; else s -= 1;
        }
      }
      if (p.inventory >= 3) s += 1;
      s += Math.max(0, (pool.length - idx) / pool.length) * 2; // respect merchandised order
      return { p: p, s: s };
    }).sort(function (a, b) { return b.s - a.s; });

    var out = [], colorSeen = {}, brandSeen = {};
    scored.forEach(function (x) {
      if (out.length >= (limit || 4)) return;
      var c = (x.p.colors || [])[0] || '_';
      var b = x.p.brand || '_';
      if (colorSeen[c]) return;
      if (brandSeen[b] >= 2) return;
      colorSeen[c] = 1; brandSeen[b] = (brandSeen[b] || 0) + 1;
      out.push(x.p);
    });
    // If diversity rules starved the row, top it up rather than show one card.
    if (out.length < (limit || 4)) {
      scored.forEach(function (x) {
        if (out.length >= (limit || 4)) return;
        if (out.indexOf(x.p) === -1) out.push(x.p);
      });
    }
    return out;
  }

  function recommendCross(limit) {
    return CROSS.filter(function (p) { return p.available && !inCart(p.handle); }).slice(0, limit || 4);
  }

  /* ------------------------------------------------------------------ drawer */

  var drawer, overlay;

  function openDrawer(reason) {
    if (!drawer) return;
    overlay.setAttribute('data-open', '1');
    drawer.setAttribute('data-open', '1');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mp-lock');
    renderDrawer();
    track('view_cart', { reason: reason || 'manual', cap_units: capUnits(cart) });
  }

  function closeDrawer() {
    if (!drawer) return;
    overlay.removeAttribute('data-open');
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mp-lock');
  }

  function miniCard(p, ctx) {
    return '<div class="mp-card" data-handle="' + esc(p.handle) + '">' +
      '<a class="mp-card__media" href="' + esc(p.url) + '"><img src="' + esc(p.img320) + '" alt="' + esc(p.title) + '" loading="lazy" width="320" height="320"></a>' +
      '<div class="mp-card__body">' +
      (p.brand ? '<span class="mp-card__brand">' + esc(p.brand) + '</span>' : '') +
      '<p class="mp-card__title">' + esc(p.title) + '</p>' +
      '<div class="mp-card__price"><span class="mp-card__now">' + money(p.price) + '</span>' +
      (p.compare > p.price ? '<span class="mp-card__was">' + money(p.compare) + '</span>' : '') + '</div>' +
      '<div class="mp-card__foot"><button type="button" class="mp-add" data-mp-add data-variant="' + esc(p.vid) +
      '" data-handle="' + esc(p.handle) + '" data-ctx="' + esc(ctx) + '">+ Add</button></div>' +
      '</div></div>';
  }

  function renderDrawer() {
    var body = $('[data-mp-cart-body]', drawer);
    var foot = $('[data-mp-cart-foot]', drawer);
    if (!body || !cart) return;

    if (!cart.items.length) {
      body.innerHTML = '<div class="mp-dr__empty">Your cart is empty.<br>Pick something to get started.</div>';
      foot.innerHTML = '';
      return;
    }

    var units = capUnits(cart);
    var cur = currentTier(units);
    var nxt = nextTier(units);
    var html = '';

    /* --- offer progress ------------------------------------------------- */
    var pct = 0, msg = '';
    if (nxt) {
      pct = Math.min(100, Math.round((units / nxt.qty) * 100));
      var need = nxt.qty - units;
      msg = '<b>' + esc(need === 1 ? CFG.copy.oneMore : CFG.copy.nMore.replace('[n]', need)) + '</b> ' +
        esc(nxt.label);
    } else if (cur) {
      pct = 100;
      msg = '<b>' + esc(cur.unlocked || (cur.label + ' unlocked')) + '</b>';
    }
    if (msg) {
      html += '<div class="mp-prog' + (!nxt && cur ? ' mp-prog--on' : '') + '">' +
        '<p class="mp-prog__t">' + msg + '</p>' +
        '<div class="mp-prog__bar"><div class="mp-prog__fill" style="width:' + pct + '%"></div></div></div>';
    }

    /* --- lines ---------------------------------------------------------- */
    cart.items.forEach(function (l) {
      var lineWas = l.original_line_price;
      var lineNow = l.final_line_price;
      html += '<div class="mp-line" data-key="' + esc(l.key) + '">' +
        '<img class="mp-line__img" src="' + esc(l.image ? l.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '') + '" alt="" width="64" height="64" loading="lazy">' +
        '<div>' +
        '<span class="mp-line__b">' + esc(l.product_type || '') + '</span>' +
        '<p class="mp-line__t">' + esc(l.product_title) + '</p>' +
        (l.variant_title && l.variant_title !== 'Default Title'
          ? '<p class="mp-line__t" style="margin-top:-3px;opacity:.6">Size ' + esc(l.variant_title) + '</p>'
          : '') +
        '<div class="mp-qty" data-mp-qty>' +
        '<button type="button" data-mp-dec aria-label="Decrease">−</button>' +
        '<span>' + l.quantity + '</span>' +
        '<button type="button" data-mp-inc aria-label="Increase">+</button>' +
        '</div></div>' +
        '<div class="mp-line__p">' + money(lineNow) +
        (lineWas > lineNow ? '<span class="mp-line__was">' + money(lineWas) + '</span>' : '') +
        '</div></div>';
    });

    /* --- cross-sell: second item first, accessories only after the goal --- */
    var recs, recHead, recEvent;
    if (nxt) {
      recs = recommendCaps(4);
      recHead = CFG.copy.recCapsHead;
      recEvent = 'second_cap_prompt_view';
    } else {
      recs = recommendCross(4);
      recHead = CFG.copy.recCrossHead;
      recEvent = 'cross_sell_view';
    }
    if (recs.length) {
      html += '<div class="mp-rec"><p class="mp-rec__h">' + esc(recHead) + '</p><div class="mp-rec__list">' +
        recs.map(function (p) { return miniCard(p, recEvent === 'second_cap_prompt_view' ? 'second_cap' : 'cross_sell'); }).join('') +
        '</div></div>';
    }

    body.innerHTML = html;

    /* --- totals: straight from Shopify ---------------------------------- */
    var discount = cart.total_discount || 0;
    var names = [];
    (cart.cart_level_discount_applications || []).forEach(function (d) { if (d.title) names.push(d.title); });
    cart.items.forEach(function (l) {
      (l.discounts || []).forEach(function (d) { if (d.title && names.indexOf(d.title) === -1) names.push(d.title); });
    });

    /* original_total_price, NOT items_subtotal_price. A collection-scoped offer
       is a product discount, so Shopify allocates it at line level and has
       already subtracted it from items_subtotal_price — pairing that with
       total_discount would print "Subtotal 1,105 − 195 = Total 1,105", which
       reads as broken. original_total_price is the pre-discount figure, so the
       three lines add up: original − total_discount === total_price, always. */
    foot.innerHTML =
      '<div class="mp-tot"><span>Subtotal</span><span>' + money(cart.original_total_price) + '</span></div>' +
      (discount > 0
        ? '<div class="mp-tot mp-tot--d"><span>' + esc(names[0] || 'Discount') + '</span><span>− ' + money(discount) + '</span></div>'
        : '') +
      '<div class="mp-tot mp-tot--g"><span>Total</span><b>' + money(cart.total_price) + '</b></div>' +
      '<button type="button" class="mp-btn mp-btn--lg" data-mp-checkout style="margin-top:12px">' +
      esc(CFG.copy.checkout) + ' • ' + money(cart.total_price) + '</button>' +
      '<p class="mp-dr__note">' + esc(CFG.copy.cartNote) + '</p>';

    if (recs.length) track(recEvent, { cap_units: units, items: recs.map(function (p) { return p.handle; }) });
  }

  /* ------------------------------------------------------------- sticky bar */

  var sticky;

  function syncSticky() {
    if (!sticky) return;
    var units = capUnits(cart);
    var nxt = nextTier(units);
    var t = $('[data-mp-sticky-txt]', sticky);
    var b = $('[data-mp-sticky-btn]', sticky);
    if (!t || !b) return;

    if (units === 0) {
      t.innerHTML = '<b>' + esc(CFG.copy.stickyIdleTitle) + '</b><span>' + esc(CFG.copy.stickyIdleSub) + '</span>';
      b.textContent = CFG.copy.stickyIdleCta;
      b.setAttribute('data-act', 'shop');
    } else if (nxt) {
      var need = nxt.qty - units;
      t.innerHTML = '<b>' + units + ' selected</b><span>' +
        esc((need === 1 ? CFG.copy.oneMore : CFG.copy.nMore.replace('[n]', need)) + ' ' + nxt.label) + '</span>';
      b.textContent = CFG.copy.stickyViewCta;
      b.setAttribute('data-act', 'cart');
    } else {
      var cur = currentTier(units);
      t.innerHTML = '<b>' + units + ' • ' + esc(cur ? cur.label : '') + '</b><span>' + money(cart ? cart.total_price : 0) + '</span>';
      b.textContent = CFG.copy.stickyCheckoutCta;
      b.setAttribute('data-act', 'cart');
    }
  }

  /* --------------------------------------------------------------- quick add */

  function handleAdd(btn) {
    var vid = btn.getAttribute('data-variant');
    if (!vid) return;
    var handle = btn.getAttribute('data-handle');
    var prev = btn.textContent;
    btn.setAttribute('data-state', 'busy');

    addToCart(vid, 1)
      .then(function () {
        lastAddedHandle = handle;
        btn.setAttribute('data-state', 'done');
        btn.textContent = CFG.copy.added;
        setTimeout(function () { btn.removeAttribute('data-state'); btn.textContent = prev; }, 1600);
        track('add_to_cart', {
          item_handle: handle,
          variant_id: vid,
          size: btn.getAttribute('data-size') || null,
          context: btn.getAttribute('data-ctx') || 'grid'
        });
        return getCart();
      })
      .then(function () { openDrawer('add'); })
      .catch(function (err) {
        var soldOut = String(err.message || '').toLowerCase().indexOf('stock') > -1;
        btn.removeAttribute('data-state');
        btn.textContent = soldOut ? 'Sold out' : prev;
        if (soldOut) btn.setAttribute('data-state', 'soldout');
      });
  }

  /* ---------------------------------------------------------- duo builder */

  var duoPicks = [];

  /* The configured slot count is a MINIMUM, not a ceiling.

     It used to be a ceiling, and picking a third cap silently dropped the
     first: the shopper's own choice deleted underneath her, on the exact
     interaction meant to grow the order. Now the row keeps one empty slot on
     the end, so there is always somewhere to put the next cap — which is the
     whole point, because the 3-cap tier is worth more to both sides than the
     2-cap one. */
  var DUO_MAX = 8;

  function duoRender(root) {
    var size = Number(root.getAttribute('data-duo-size') || 2);
    var slots = $('[data-mp-duo-slots]', root);
    var sum = $('[data-mp-duo-sum]', root);
    var cta = $('[data-mp-duo-cta]', root);
    if (!slots) return;

    var shown = Math.max(size, duoPicks.length + 1);
    if (shown > DUO_MAX) shown = DUO_MAX;

    var sh = '';
    for (var i = 0; i < shown; i++) {
      var p = duoPicks[i];
      if (p) {
        sh += '<div class="mp-slot mp-slot--filled"><button type="button" class="mp-slot__x" data-mp-duo-remove="' + i + '" aria-label="Remove">×</button>' +
          '<img src="' + esc(p.img320) + '" alt="' + esc(p.title) + '" width="320" height="320"></div>';
      } else {
        sh += '<div class="mp-slot"><span class="mp-slot__n">' + esc(CFG.duoSlotLabel || 'Cap') + ' ' + (i + 1) + '</span><span class="mp-slot__hint">' +
          esc(CFG.copy.duoSlotHint) + '</span></div>';
      }
    }
    slots.innerHTML = sh;
    // Keep the row square-ish as it grows; the stylesheet only knows about two.
    slots.style.gridTemplateColumns = 'repeat(' + Math.min(Math.max(shown, 2), 4) + ',1fr)';

    var gross = duoPicks.reduce(function (n, p) { return n + p.price; }, 0);
    var tier = currentTier(duoPicks.length);
    var up = nextTier(duoPicks.length);
    var rate = tier ? Number(tier.rate || 0) : 0;
    var off = Math.round(gross * rate);

    if (sum) {
      if (!duoPicks.length) {
        sum.innerHTML = '<div class="mp-duo__row"><span>' + esc(CFG.copy.duoEmpty) + '</span></div>';
      } else {
        sum.innerHTML =
          '<div class="mp-duo__row"><span>' + duoPicks.length + ' selected</span><span>' + money(gross) + '</span></div>' +
          (off > 0
            ? '<div class="mp-duo__row"><span class="mp-duo__save">' + esc(tier.label) + '</span><span class="mp-duo__save">− ' + money(off) + '</span></div>'
            : '') +
          '<div class="mp-duo__row mp-duo__row--tot"><span>' + (off > 0 ? 'Your total' : 'Total') + '</span><b>' +
          money(gross - off) + (off > 0 ? ' <span class="mp-duo__was" style="font-size:13px">' + money(gross) + '</span>' : '') + '</b></div>' +
          '<p class="mp-duo__note">' + esc(off > 0 ? CFG.copy.duoAppliedNote : CFG.copy.duoPendingNote) +
          /* The nudge is built from the tier data, which mirrors the real
             Shopify discounts, so it can never promise a rate checkout will
             not honour. */
          (up ? ' ' + esc('Add ' + (up.qty - duoPicks.length) + ' more for ' + up.label + '.') : '') + '</p>';
      }
    }
    if (cta) {
      cta.disabled = duoPicks.length === 0;
      if (!duoPicks.length) {
        cta.textContent = CFG.copy.duoCtaEmpty;
      } else if (duoPicks.length < size) {
        cta.textContent = CFG.copy.duoCtaPartial.replace('[n]', size - duoPicks.length);
      } else if (duoPicks.length > size && CFG.copy.duoCtaFull.indexOf('[n]') === -1) {
        /* Merchant copy for a full bundle reads "Add both to cart", which is
           wrong the moment there are three. Put [n] in the setting to control
           the wording; otherwise this states the count plainly. */
        cta.textContent = 'Add ' + duoPicks.length + ' caps to cart';
      } else {
        cta.textContent = CFG.copy.duoCtaFull.replace('[n]', duoPicks.length);
      }
    }

    $$('[data-mp-pick]', root).forEach(function (b) {
      var card = b.closest('.mp-card');
      if (!card) return;
      var h = card.getAttribute('data-handle');
      var on = duoPicks.some(function (p) { return p.handle === h; });
      card.classList.toggle('mp-card--picked', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function duoToggle(root, handle) {
    var size = Number(root.getAttribute('data-duo-size') || 2);
    var idx = duoPicks.map(function (p) { return p.handle; }).indexOf(handle);
    if (idx > -1) { duoPicks.splice(idx, 1); }
    else {
      var p = CATALOG.filter(function (x) { return x.handle === handle; })[0];
      if (!p) return;
      // No silent eviction of an earlier pick. Only a sanity ceiling.
      if (duoPicks.length >= DUO_MAX) return;
      duoPicks.push(p);
      if (duoPicks.length === 1) track('bundle_started', { item_handle: handle });
      /* Exactly size, not >=. Now that the row grows past the slot count, a
         >= test would log bundle_completed again on the third cap and the
         fourth, inflating the one event you would measure the funnel on. */
      if (duoPicks.length === size) track('bundle_completed', { items: duoPicks.map(function (x) { return x.handle; }) });
    }
    duoRender(root);
  }

  function duoAdd(root) {
    if (!duoPicks.length) return;
    var cta = $('[data-mp-duo-cta]', root);
    if (cta) cta.disabled = true;
    fetch(CFG.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: duoPicks.map(function (p) { return { id: Number(p.vid), quantity: 1 }; }) })
    }).then(function (r) { return r.json(); })
      .then(function () {
        track('add_to_cart', { context: 'bundle', items: duoPicks.map(function (p) { return p.handle; }) });
        lastAddedHandle = duoPicks[duoPicks.length - 1].handle;
        duoPicks = [];
        duoRender(root);
        return getCart();
      })
      .then(function () { openDrawer('bundle'); })
      .catch(function () { if (cta) cta.disabled = false; });
  }

  /* ------------------------------------------------------------ chip filters */

  function applyFilters(root) {
    var active = {};
    $$('[data-mp-chip][aria-pressed=true]', root).forEach(function (c) {
      var g = c.getAttribute('data-group');
      (active[g] = active[g] || []).push(c.getAttribute('data-value'));
    });
    var groups = Object.keys(active);
    var shown = 0;
    $$('.mp-card', root).forEach(function (card) {
      var ok = groups.every(function (g) {
        var have = (card.getAttribute('data-' + g) || '').split('|').filter(Boolean);
        // A chip may stand for several underlying tags (a "style" chip is just a
        // group of brands), so its data-value can be comma-separated.
        return active[g].some(function (v) {
          return v.split(',').some(function (one) { return have.indexOf(one.trim()) > -1; });
        });
      });
      card.hidden = !ok;
      if (ok) shown++;
    });
    var empty = $('[data-mp-empty]', root);
    if (empty) empty.hidden = shown > 0;
    var count = $('[data-mp-shown]', root);
    if (count) {
      /* With no filter on, the header states how many products exist in the
         collection, not how many happen to be rendered — Load more must not
         make the number appear to grow. The unit word (caps / pairs) is read
         off data-total so one section serves every catalogue. */
      var total = count.getAttribute('data-total') || '';
      var unit = total.replace(/^[\d,\s]+/, '') || 'items';
      count.textContent = groups.length ? (shown + ' ' + unit) : (total || shown + ' ' + unit);
    }
    if (groups.length) track('filter_caps', { filters: active, results: shown });
  }

  /* ---------------------------------------------------------- load more */

  /* Pulls the next page of the grid through Shopify's Section Rendering API
     and appends just the cards. Keeps scroll position, keeps cart state, and
     lets the first paint stay small while everything in the collection is still
     one tap away. Newly appended cards need no wiring — every handler on this
     page is delegated from document. */
  function loadMore(btn) {
    var scope = btn.closest('[data-mp-filterable]');
    var grid = scope && scope.querySelector('.mp-grid');
    if (!grid) return;

    var label = btn.getAttribute('data-label') || btn.textContent;
    btn.disabled = true;
    btn.textContent = (btn.getAttribute('data-loading') || 'Loading') + '…';

    var url = window.location.pathname +
      '?section_id=' + encodeURIComponent(btn.getAttribute('data-section')) +
      '&page=' + encodeURIComponent(btn.getAttribute('data-page'));

    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cards = doc.querySelectorAll('.mp-card');
        if (!cards.length) { btn.remove(); return; }

        var frag = document.createDocumentFragment();
        Array.prototype.forEach.call(cards, function (c) {
          frag.appendChild(document.importNode(c, true));
        });
        var empty = grid.querySelector('[data-mp-empty]');
        if (empty) grid.insertBefore(frag, empty); else grid.appendChild(frag);

        var next = doc.querySelector('[data-mp-loadmore]');
        if (next) {
          btn.setAttribute('data-page', next.getAttribute('data-page'));
          btn.disabled = false;
          btn.textContent = label;
        } else {
          // Last page. Swap in the end-of-list link if the section shipped one.
          var tail = doc.querySelector('a.mp-more');
          if (tail && btn.parentNode) btn.parentNode.replaceChild(document.importNode(tail, true), btn);
          else btn.remove();
        }

        applyFilters(scope);
        track('load_more_caps', {
          list_name: grid.getAttribute('data-list-name') || 'caps',
          rendered: grid.querySelectorAll('.mp-card').length
        });
      })
      .catch(function () { btn.disabled = false; btn.textContent = label; });
  }

  /* -------------------------------------------------- filter across the lot */

  /* Chip filters can only see cards that are in the DOM. On a 268-product
     collection paginated 16 at a time, tapping "Gucci" would hide 15 cards and
     show one — which reads as "MRPIECE has one Gucci cap". Wrong, and worse
     than no filter at all.

     So the first time a shopper actually engages a filter, pull the rest of the
     collection in behind the scenes, then filter across all of it. The wait is
     paid once, only by shoppers who filter, and every filter after that is
     instant.

     Pages are fetched in small parallel batches and the loop stops as soon as a
     batch comes back empty, so it needs no page count from Liquid and cannot
     silently stop half way through a collection. */
  /* 30 pages was enough for every catalogue as configured today, but only just:
     642 slides at 24 a page is 27 requests, and dropping that page size to 16
     would need 41 and quietly filter across part of the collection. The loop
     stops the moment a batch comes back empty, so a higher ceiling costs
     nothing on a smaller collection — it only removes the trap. */
  var MAX_PAGES = 60;
  var BATCH = 6;

  function ensureAllLoaded(scope) {
    var btn = scope.querySelector('[data-mp-loadmore]');
    if (!btn) return Promise.resolve();
    if (scope.__mpAll) return scope.__mpAll;

    var grid = scope.querySelector('.mp-grid');
    if (!grid) return Promise.resolve();

    var sec = btn.getAttribute('data-section');
    var from = Number(btn.getAttribute('data-page')) || 2;
    var label = btn.getAttribute('data-label') || btn.textContent;

    btn.disabled = true;
    btn.textContent = (btn.getAttribute('data-loading') || 'Loading') + '…';

    function grab(n) {
      return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sec) + '&page=' + n,
        { headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return { n: n, cards: doc.querySelectorAll('.mp-card') };
        })
        .catch(function () { return { n: n, cards: [] }; });
    }

    function append(results) {
      var empty = grid.querySelector('[data-mp-empty]');
      results.sort(function (a, b) { return a.n - b.n; }).forEach(function (r) {
        if (!r.cards.length) return;
        var frag = document.createDocumentFragment();
        Array.prototype.forEach.call(r.cards, function (c) {
          frag.appendChild(document.importNode(c, true));
        });
        if (empty) grid.insertBefore(frag, empty); else grid.appendChild(frag);
      });
    }

    function run(start) {
      if (start > MAX_PAGES) return Promise.resolve(false);
      var group = [];
      for (var n = start; n < start + BATCH && n <= MAX_PAGES; n++) group.push(n);
      return Promise.all(group.map(grab)).then(function (results) {
        append(results);
        // Keep going only while the last page of the batch still had product.
        var tail = results[results.length - 1];
        if (tail && tail.cards.length) return run(start + BATCH);
        return true;
      });
    }

    scope.__mpAll = run(from)
      .then(function (finished) {
        var b = scope.querySelector('[data-mp-loadmore]');
        if (!b) return;
        if (finished === false) {
          // Hit the safety cap on a very large collection: let them keep going.
          b.setAttribute('data-page', String(MAX_PAGES + 1));
          b.disabled = false;
          b.textContent = label;
        } else {
          b.remove();
        }
      })
      .then(function () {
        track('load_all_for_filter', {
          list_name: grid.getAttribute('data-list-name') || 'grid',
          rendered: grid.querySelectorAll('.mp-card').length
        });
      })
      .catch(function () {
        var b = scope.querySelector('[data-mp-loadmore]');
        if (b) { b.disabled = false; b.textContent = label; }
      });

    return scope.__mpAll;
  }

  /* --------------------------------------------------------------- quick view */

  function openQuickView(handle, fallbackUrl) {
    var p = CATALOG.filter(function (x) { return x.handle === handle; })[0];
    var qv = $('[data-mp-qv]');
    if (!p) { if (fallbackUrl) window.location.href = fallbackUrl; return; }
    if (!qv) return;
    $('[data-mp-qv-in]', qv).innerHTML =
      '<div class="mp-qv__img"><img src="' + esc(p.img800) + '" alt="' + esc(p.title) + '" width="800" height="800"></div>' +
      '<div class="mp-qv__c">' +
      (p.brand ? '<span class="mp-card__brand">' + esc(p.brand) + '</span>' : '') +
      '<h3>' + esc(p.title) + '</h3>' +
      '<div class="mp-card__price"><span class="mp-card__now" style="font-size:24px">' + money(p.price) + '</span>' +
      (p.compare > p.price ? '<span class="mp-card__was" style="font-size:14px">' + money(p.compare) + '</span>' : '') + '</div>' +
      '<div class="mp-card__meta" style="margin:9px 0 14px">' + esc(p.sizeLabel || '') +
      (p.inventory > 0 && p.inventory <= (CFG.lowStockAt || 3) ? ' • Only ' + p.inventory + ' left' : '') + '</div>' +
      '<button type="button" class="mp-btn mp-btn--lg" data-mp-add data-variant="' + esc(p.vid) + '" data-handle="' + esc(p.handle) + '" data-ctx="quick_view"' +
      (p.available ? '' : ' disabled') + '>' + (p.available ? esc(CFG.copy.qvAdd) : 'Sold out') + '</button>' +
      '<a class="mp-qv__link" href="' + esc(p.url) + '">' + esc(CFG.copy.qvFull) + '</a>' +
      '</div>';
    qv.setAttribute('data-open', '1');
    document.documentElement.classList.add('mp-lock');
    track('quick_view', { item_handle: handle });
    track('select_item', { item_handle: handle });
  }

  function closeQuickView() {
    var qv = $('[data-mp-qv]');
    if (!qv) return;
    qv.removeAttribute('data-open');
    if (!drawer || drawer.getAttribute('data-open') !== '1') document.documentElement.classList.remove('mp-lock');
  }

  /* ------------------------------------------------------------------- boot */

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function init() {
    if (BOOT) return;
    var cfg = readJSON('mp-caps-config');
    if (!cfg) return;
    BOOT = true;
    CFG = cfg;
    CATALOG = readJSON('mp-caps-catalog') || [];
    CROSS = readJSON('mp-caps-cross') || [];

    drawer = $('[data-mp-cart]');
    overlay = $('[data-mp-overlay]');
    sticky = $('[data-mp-sticky]');

    getCart();

    /* one delegated listener for the whole page */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var add = t.closest('[data-mp-add]');
      if (add) { e.preventDefault(); handleAdd(add); return; }

      var pick = t.closest('[data-mp-pick]');
      if (pick) {
        e.preventDefault();
        var root = pick.closest('[data-mp-duo]');
        var card = pick.closest('.mp-card');
        if (root && card) duoToggle(root, card.getAttribute('data-handle'));
        return;
      }

      var rm = t.closest('[data-mp-duo-remove]');
      if (rm) {
        e.preventDefault();
        duoPicks.splice(Number(rm.getAttribute('data-mp-duo-remove')), 1);
        duoRender(rm.closest('[data-mp-duo]'));
        return;
      }

      var duoCta = t.closest('[data-mp-duo-cta]');
      if (duoCta) { e.preventDefault(); duoAdd(duoCta.closest('[data-mp-duo]')); return; }

      var chip = t.closest('[data-mp-chip]');
      if (chip) {
        e.preventDefault();
        var on = chip.getAttribute('aria-pressed') === 'true';
        var scope = chip.closest('[data-mp-filterable]');
        if (chip.getAttribute('data-value') === '*') {
          $$('[data-mp-chip][data-group="' + chip.getAttribute('data-group') + '"]', scope)
            .forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
        } else {
          var allChip = $('[data-mp-chip][data-group="' + chip.getAttribute('data-group') + '"][data-value="*"]', scope);
          if (allChip) allChip.setAttribute('aria-pressed', 'false');
          chip.setAttribute('aria-pressed', on ? 'false' : 'true');
        }
        /* Filter what is already on screen straight away so the tap feels
           instant, then pull the rest of the collection and filter again so the
           result is the whole catalogue, not just page one. */
        applyFilters(scope);
        if ($$('[data-mp-chip][aria-pressed=true]', scope).length) {
          ensureAllLoaded(scope).then(function () { applyFilters(scope); });
        }
        return;
      }

      var lm = t.closest('[data-mp-loadmore]');
      if (lm) { e.preventDefault(); loadMore(lm); return; }

      var qvBtn = t.closest('[data-mp-quickview]');
      if (qvBtn) {
        e.preventDefault();
        var qvCard = qvBtn.closest('.mp-card');
        var lnk = qvCard && qvCard.querySelector('.mp-card__title a');
        openQuickView(qvBtn.getAttribute('data-handle'), lnk ? lnk.getAttribute('href') : null);
        return;
      }

      if (t.closest('[data-mp-qv-close]')) { closeQuickView(); return; }
      if (t.closest('[data-mp-cart-open]')) { e.preventDefault(); openDrawer('button'); return; }
      if (t.closest('[data-mp-cart-close]')) { closeDrawer(); return; }

      var checkout = t.closest('[data-mp-checkout]');
      if (checkout) {
        e.preventDefault();
        track('begin_checkout', {
          value: (cart ? cart.total_price : 0) / 100,
          currency: CFG.currency,
          cap_units: capUnits(cart),
          items: cart ? cart.items.map(function (l) { return l.handle; }) : []
        });
        window.location.href = '/checkout';
        return;
      }

      var stickyBtn = t.closest('[data-mp-sticky-btn]');
      if (stickyBtn) {
        e.preventDefault();
        if (stickyBtn.getAttribute('data-act') === 'shop') {
          var g = $('[data-mp-scroll-target]');
          if (g) g.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else { openDrawer('sticky'); }
        return;
      }

      var inc = t.closest('[data-mp-inc]');
      var dec = t.closest('[data-mp-dec]');
      if (inc || dec) {
        e.preventDefault();
        var line = (inc || dec).closest('.mp-line');
        var key = line.getAttribute('data-key');
        var item = cart.items.filter(function (l) { return l.key === key; })[0];
        if (!item) return;
        changeLine(key, item.quantity + (inc ? 1 : -1)).then(renderDrawer);
        return;
      }

      /* On this page the campaign drawer IS the cart. Send the theme header's
         bag icon here too, so a shopper who taps it sees the offer progress
         instead of the theme's generic drawer. Scoped to links outside our own
         markup; if the theme uses a custom element rather than a link this just
         doesn't match and the theme drawer opens, which is still correct. */
      var bag = t.closest('a[href$="/cart"]');
      if (bag && !t.closest('.mp')) { e.preventDefault(); openDrawer('header'); return; }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeQuickView(); closeDrawer();
    });

    /* duo builders */
    $$('[data-mp-duo]').forEach(function (r) { duoRender(r); });

    /* sticky bar appears once the shopper is past the hero */
    if (sticky) {
      var trip = $('[data-mp-scroll-target]') || document.body;
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (en) {
          sticky.setAttribute('data-show', en.isIntersecting || en.boundingClientRect.top < 0 ? '1' : '0');
        });
      }, { rootMargin: '0px 0px -70% 0px' });
      io.observe(trip);
      syncSticky();
    }

    /* view_item_list once per grid, when it actually reaches the screen */
    if ('IntersectionObserver' in window) {
      var lo = new IntersectionObserver(function (es, obs) {
        es.forEach(function (en) {
          if (!en.isIntersecting) return;
          obs.unobserve(en.target);
          track('view_item_list', {
            list_name: en.target.getAttribute('data-list-name') || 'caps',
            items: $$('.mp-card', en.target).slice(0, 24).map(function (c) { return c.getAttribute('data-handle'); })
          });
        });
      }, { threshold: 0.15 });
      $$('[data-mp-list]').forEach(function (l) { lo.observe(l); });
    }

    track('landing_page_view', { page: 'caps_sale' });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
